import { useEffect, useState } from "react";
import { toast } from "sonner";
import { updateConfig, errText, usd } from "@/lib/api";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const COINS = ["BTC", "ETH", "SOL"];
const NUMS = [
  ["trade_amount_usd", "Wager per trade ($)", "0.01"],
  ["trades_per_cycle", "Trades per cycle", "1"],
  ["max_concurrent_trades", "Max open at once", "1"],
  ["max_spread_cents", "Max buy/sell gap (¢)", "1"],
  ["profit_exit_multiplier", "Profit exit multiplier (x)", "0.1"],
  ["max_hold_time_secs", "Max hold time (s)", "0.1"],
  ["trigger_pause_secs", "Pause after trigger (s)", "0.1"],
  ["daily_loss_cap_usd", "Daily loss cap ($)", "1"],
];
const inp = "mt-1 w-full bg-transparent border border-[var(--border-active)] rounded px-2 py-1.5 font-mono-num text-sm text-[var(--text)]";
const lbl = "text-[10px] uppercase tracking-widest text-[var(--text-mute)]";

function Field({ label, children }) {
  return <label className={lbl}>{label}{children}</label>;
}

function CoinRow({ a, f, set }) {
  const k = a.toLowerCase();
  const put = (key, v) => set({ ...f, [key]: { ...f[key], [a]: v } });
  return (
    <tr className="border-t border-[var(--border-subtle)]">
      <td className="py-1.5 pr-3 font-head font-bold">{a}</td>
      <td className="pr-3"><input type="checkbox" data-testid={`cfg-${k}-on`} checked={!!f.active_markets[a]} className="w-4 h-4 accent-emerald-400" onChange={(e) => put("active_markets", e.target.checked)} /></td>
      <td className="pr-3"><input type="number" step="0.01" data-testid={`cfg-${k}-threshold`} value={f.velocity_threshold[a]} className={inp} onChange={(e) => put("velocity_threshold", e.target.value)} /></td>
      <td className="pr-3"><input type="number" step="1" data-testid={`cfg-${k}-ceiling`} value={f.max_entry_cents[a]} className={inp} onChange={(e) => put("max_entry_cents", e.target.value)} /></td>
      <td><input type="number" step="1" data-testid={`cfg-${k}-depth`} value={f.min_contract_depth[a]} className={inp} onChange={(e) => put("min_contract_depth", e.target.value)} /></td>
    </tr>
  );
}

const toNum = (o) => Object.fromEntries(Object.entries(o).map(([k, v]) => [k, Number(v)]));

export default function SettingsPanel({ config }) {
  const [f, setF] = useState(null);
  useEffect(() => { if (config && !f) setF(structuredClone(config)); }, [config, f]);
  if (!f) return null;
  const bad = NUMS.find(([k]) => f[k] === "" || Number.isNaN(Number(f[k])))
    || COINS.flatMap((a) => ["velocity_threshold", "max_entry_cents", "min_contract_depth"].map((k) => f[k][a])).some((v) => v === "" || Number.isNaN(Number(v)))
    || f.scan_windows.some((v) => v === "" || Number.isNaN(Number(v)));
  const save = async () => {
    if (bad) { toast.error("Every number field needs a value"); return; }
    const patch = {
      ...Object.fromEntries(NUMS.map(([k]) => [k, Number(f[k])])),
      trading_schedule_active: f.trading_schedule_active, schedule_open_day: Number(f.schedule_open_day), schedule_open_time: f.schedule_open_time,
      schedule_close_day: Number(f.schedule_close_day), schedule_close_time: f.schedule_close_time,
      start_scan_window: f.start_scan_window, end_scan_window: f.end_scan_window, active_markets: f.active_markets,
      scan_windows: f.scan_windows.map(Number), velocity_threshold: toNum(f.velocity_threshold),
      max_entry_cents: toNum(f.max_entry_cents), min_contract_depth: toNum(f.min_contract_depth),
    };
    try { const { data } = await updateConfig(patch); setF(structuredClone(data)); toast.success("Settings saved"); }
    catch (e) { toast.error(errText(e)); }
  };
  const cycleTotal = Number(f.trade_amount_usd) * (Number(f.trades_per_cycle) || 0);
  return (
    <section data-testid="settings-panel" className="rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-card)] p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-head font-bold text-base md:text-lg">Control panel</h2>
        <button data-testid="settings-save-btn" onClick={save} className="px-4 py-1.5 rounded-full border border-cyan-500 text-cyan-100 text-sm hover:bg-cyan-500/15 transition-colors">Save settings</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
        <label className={`${lbl} flex items-center gap-2 col-span-2 md:col-span-1`}>
          <input type="checkbox" data-testid="cfg-schedule-active" checked={f.trading_schedule_active} className="w-4 h-4 accent-emerald-400" onChange={(e) => setF({ ...f, trading_schedule_active: e.target.checked })} />Weekly schedule
        </label>
        <Field label="Opens (Pacific)">
          <div className="flex gap-1"><select data-testid="cfg-open-day" value={f.schedule_open_day} className={inp} onChange={(e) => setF({ ...f, schedule_open_day: e.target.value })}>{DAYS.map((d, i) => <option key={d} value={i} className="bg-black">{d}</option>)}</select>
            <input data-testid="cfg-open-time" type="time" value={f.schedule_open_time} className={inp} onChange={(e) => setF({ ...f, schedule_open_time: e.target.value })} /></div>
        </Field>
        <Field label="Closes (Pacific)">
          <div className="flex gap-1"><select data-testid="cfg-close-day" value={f.schedule_close_day} className={inp} onChange={(e) => setF({ ...f, schedule_close_day: e.target.value })}>{DAYS.map((d, i) => <option key={d} value={i} className="bg-black">{d}</option>)}</select>
            <input data-testid="cfg-close-time" type="time" value={f.schedule_close_time} className={inp} onChange={(e) => setF({ ...f, schedule_close_time: e.target.value })} /></div>
        </Field>
        <Field label="Scan starts (mm:ss)"><input data-testid="cfg-scan-start" value={f.start_scan_window} className={inp} onChange={(e) => setF({ ...f, start_scan_window: e.target.value })} /></Field>
        <Field label="Scan ends (mm:ss)"><input data-testid="cfg-scan-end" value={f.end_scan_window} className={inp} onChange={(e) => setF({ ...f, end_scan_window: e.target.value })} /></Field>
        <Field label="Lookbacks (s)">
          <div className="flex gap-1">{f.scan_windows.map((v, i) => (
            <input key={i} data-testid={`cfg-scan-window-${i + 1}`} type="number" step="0.1" value={v} className={inp}
              onChange={(e) => setF({ ...f, scan_windows: f.scan_windows.map((x, j) => (j === i ? e.target.value : x)) })} />))}</div>
        </Field>
      </div>
      <table data-testid="cfg-coins-table" className="w-full max-w-3xl text-sm">
        <thead className={`${lbl} text-left`}><tr><th>Coin</th><th>On</th><th>Move needed ($)</th><th>Max entry (¢)</th><th>Min contracts</th></tr></thead>
        <tbody>{COINS.map((a) => <CoinRow key={a} a={a} f={f} set={setF} />)}</tbody>
      </table>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {NUMS.map(([k, label, step]) => (
          <Field key={k} label={label}><input data-testid={`cfg-${k.replaceAll("_", "-")}`} type="number" step={step} value={f[k]} className={inp} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></Field>
        ))}
      </div>
      <p data-testid="cfg-per-trade" className="text-xs text-[var(--text-dim)]">Each trade wagers up to {usd(Number(f.trade_amount_usd))}, fees included · up to {usd(cycleTotal)} per 15-min cycle ({f.trades_per_cycle} trades).</p>
    </section>
  );
}
